import { Link, useSearchParams } from 'react-router-dom'
import { Home, Mail, Phone, MapPin, Send, AlertCircle, Instagram } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { usePageTitle, setDocumentTitle } from '../utils/translation'

export default function Contact() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const pageTitle = usePageTitle('pages.contact.title', 'Contact Us')
  
  // Check if user came from phone number change help
  const fromPhoneChange = searchParams.get('from') === 'phone-change'
  
  useEffect(() => {
    setDocumentTitle(pageTitle)
  }, [pageTitle])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="flex items-center px-4 py-4">
          <Link 
            to={fromPhoneChange ? "/login" : "/"} 
            className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-600 mr-4 shadow-sm hover:bg-amber-700 transition-colors"
            aria-label={fromPhoneChange ? "Return to login" : "Return to home"}
          >
            <Home size={20} className="text-white" />
          </Link>
          <h1 className="font-bold text-xl text-amber-800">{t('pages.contact.title')}</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-5 py-8">
        {/* Phone Number Change Help Section */}
        {fromPhoneChange && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0 mt-1">
                <AlertCircle className="w-5 h-5 text-yellow-700" />
              </div>
              <div className="ml-4">
                <h2 className="text-lg font-bold text-yellow-900 mb-3">
                  {t('pages.contact.phoneChangeHelp.title', 'Phone Number Change Help')}
                </h2>
                <div className="text-yellow-800 space-y-2 text-sm">
                  <p>{t('pages.contact.phoneChangeHelp.description', 'If you\'ve changed your phone number and can\'t login to your account, our customer service team can help you update your account information.')}</p>
                  <p className="font-semibold">{t('pages.contact.phoneChangeHelp.instructions', 'To resolve this issue, please contact us using one of the methods below and provide:')}</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>{t('pages.contact.phoneChangeHelp.oldPhone', 'Your old phone number (if you remember it)')}</li>
                    <li>{t('pages.contact.phoneChangeHelp.newPhone', 'Your new phone number')}</li>
                    <li>{t('pages.contact.phoneChangeHelp.email', 'Email address associated with your account (if any)')}</li>
                    <li>{t('pages.contact.phoneChangeHelp.orderInfo', 'Any previous order information or subscription details')}</li>
                  </ul>
                  <p className="font-semibold text-yellow-900">{t('pages.contact.phoneChangeHelp.responseTime', 'Our team will typically respond within 24 hours and help you regain access to your account.')}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Contact Information Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-amber-900 mb-6">{t('pages.contact.getInTouch')}</h2>
          
          <div className="space-y-5">
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                <Mail className="w-5 h-5 text-amber-700" />
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-amber-900">{t('pages.contact.emailUs')}</h3>
                <p className="text-amber-800">{t('pages.contact.emailAddress')}</p>
                <p className="text-sm text-amber-600 mt-1">{t('pages.contact.emailResponseTime')}</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                <Phone className="w-5 h-5 text-amber-700" />
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-amber-900">{t('pages.contact.lineContact')}</h3>
                <p className="text-amber-800">
                  <a href="https://line.me/ti/p/@piika" target="_blank" rel="noopener noreferrer" className="hover:underline">
                    piika
                  </a>
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                <Instagram className="w-5 h-5 text-amber-700" />
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-amber-900">{t('pages.contact.instagramContact')}</h3>
                <p className="text-amber-800">
                  <a href="https://www.instagram.com/piika.co/" target="_blank" rel="noopener noreferrer" className="hover:underline">
                    @piika.co
                  </a>
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                <MapPin className="w-5 h-5 text-amber-700" />
              </div>
              <div className="ml-4">
                <h3 className="font-semibold text-amber-900">{t('pages.contact.visitUs')}</h3>
                <p className="text-amber-800">{t('pages.contact.visitUsAddress1')}</p>
                <p className="text-amber-800">{t('pages.contact.visitUsAddress2')}</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white py-6 px-5 border-t border-amber-100">
        <div className="flex flex-col items-center space-y-4">
          <p className="text-center text-xs text-amber-600">
            &copy; {new Date().getFullYear()} {t('pages.contact.footer.copyright')}
          </p>
          <div className="flex space-x-4">
            <a href="/terms" className="text-xs text-amber-700">{t('pages.contact.footer.terms')}</a>
            <a href="/privacy" className="text-xs text-amber-700">{t('pages.contact.footer.privacy')}</a>
            <a href="/contact" className="text-xs text-amber-700 font-bold">{t('pages.contact.footer.contact')}</a>
          </div>
        </div>
      </footer>
    </div>
  )
} 